import { captureEvent } from "@/features/analytics/posthog";
import type { OnboardingInstallType, OnboardingStep } from "./types";

/**
 * The onboarding's product events — the only record of how long a new customer
 * takes to reach their agent, and where they stop.
 *
 * One name per moment, never per screen variant, so a funnel can be built on
 * them without a list of aliases. The wizard runs outside `BackofficeLayout`,
 * where the `company` group is set, so every event carries its `client_id`
 * itself. Nothing here carries what the client typed: steps, statuses and
 * durations only.
 */

/** A step came on screen. `from` is the step it replaced, when there was one. */
export const trackStepViewed = (
  clientId: string,
  step: OnboardingStep,
  from?: { step: OnboardingStep; durationMs: number },
) => {
  captureEvent("onboarding_step_viewed", { client_id: clientId, step });
  if (from) {
    captureEvent("onboarding_step_completed", {
      client_id: clientId,
      step: from.step,
      next_step: step,
      duration_ms: Math.round(from.durationMs),
    });
  }
};

/**
 * The website analysis came back, one way or the other. `durationMs` runs from
 * when the server started it, so it is the wait the client actually had behind
 * the questions, not the time the page happened to be open.
 */
export const trackAnalysisOutcome = (
  clientId: string,
  outcome: "done" | "failed",
  durationMs: number | null,
  seenOn: OnboardingStep,
) =>
  captureEvent(`onboarding_analysis_${outcome}`, {
    client_id: clientId,
    duration_ms: durationMs === null ? null : Math.round(durationMs),
    // Which screen the client was on when it landed — "waiting" means they
    // outran it.
    step: seenOn,
  });

export const trackCompleted = (
  clientId: string,
  result: {
    installType?: OnboardingInstallType;
    hasDemoLine: boolean;
    startedAt: number | null;
  },
) =>
  captureEvent("onboarding_completed", {
    client_id: clientId,
    install_type: result.installType ?? null,
    has_demo_line: result.hasDemoLine,
    duration_ms: result.startedAt ? Date.now() - result.startedAt : null,
  });

/** A write the client was waiting on failed; `kind` says which. */
export const trackError = (
  clientId: string,
  kind: "load" | "analysis_start" | "mobile" | "complete",
  step: OnboardingStep,
) => captureEvent("onboarding_error", { client_id: clientId, kind, step });
